import React, { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { ESignUp } from "@layouts/SecondRootLayout";

import AccountRestoreModal from "@/components/AccountRestoreModal";
import SignUpDialog from "@/components/SignUpDialog";
import AutoReleaseChoiceModal from "@/pages/AccountLicensesPage/AutoReleaseChoiceModal";
import { DowngradeChannelPickerModal } from "@/pages/AccountLicensesPage/LicensesTab";

/**
 * DEV-ONLY modal launcher. Opens an otherwise flow-gated modal directly via a
 * URL param so it can be reviewed on the live dev site (incl. on a phone)
 * without walking the whole flow.
 *
 *   ?devmodal=welcome           → SignUpDialog Welcome step
 *   ?devmodal=account-restore   → AccountRestoreModal (login-time grace recovery)
 *   ?devmodal=downgrade-picker  → DowngradeChannelPickerModal (pick channels to drop)
 *   ?devmodal=auto-release      → AutoReleaseChoiceModal (post-plan-change choice)
 *
 * (verify-email / delete-code / password-code were removed once signed off —
 * re-add a case here if a modal needs review again.) Gated to the dev host so
 * it can never surface on production — NODE_ENV is "production" in the dev
 * *build*, so we key off the hostname. Use the floating "Close preview" button
 * to dismiss (some of these have no in-modal ✕).
 */
const PREVIEW_EMAIL = "creator@example.com";
const isDevHost = (() => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h.startsWith("dev.") || h.includes(".dev.");
})();

const DevModalLauncher: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const key = params.get("devmodal");

  const close = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("devmodal");
    setParams(next, { replace: true });
  }, [params, setParams]);

  if (!isDevHost || !key) return null;

  let modal: React.ReactNode = null;
  switch (key) {
    case "welcome":
      modal = <SignUpDialog open step={ESignUp.Welcome} handleClose={close} handleNextStep={close} devPreview />;
      break;
    case "account-restore":
      // sessionTokens=null → the modal skips setSession; the Recover/Logout
      // buttons still call real actions, so prefer the floating Close button.
      modal = (
        <AccountRestoreModal
          open
          permanentDeleteOn="July 15, 2026"
          userEmail={PREVIEW_EMAIL}
          hadSubscription
          subEndsOn="July 1, 2026"
          sessionTokens={null}
          onClose={close}
        />
      );
      break;
    case "downgrade-picker":
      modal = (
        <DowngradeChannelPickerModal
          open
          channels={[
            { id: "ch1", channelName: "Wanderer Diaries", channelHandle: "@wandererdiaries", channelThumbnailUrl: null },
            { id: "ch2", channelName: "Northern Light Studio", channelHandle: "@northernlight", channelThumbnailUrl: null },
            { id: "ch3", channelName: "Camino Stories", channelHandle: "@caminostories", channelThumbnailUrl: null },
            { id: "ch4", channelName: "Ambient Atlas", channelHandle: "@ambientatlas", channelThumbnailUrl: null },
            { id: "ch5", channelName: "Choir & Chant", channelHandle: "@choirandchant", channelThumbnailUrl: null },
            { id: "ch6", channelName: "Film Score Lab", channelHandle: "@filmscorelab", channelThumbnailUrl: null },
            { id: "ch7", channelName: "Slow TV Nordics", channelHandle: "@slowtvnordics", channelThumbnailUrl: null },
            { id: "ch8", channelName: "Meditation Daily", channelHandle: "@meditationdaily", channelThumbnailUrl: null },
          ]}
          newLimit={2}
          effectiveDate="August 1, 2026"
          onConfirm={close}
          onCancelDowngrade={close}
          onClose={close}
        />
      );
      break;
    case "auto-release":
      // Self-managing modal; devPreview forces it open with mock data (no API).
      modal = <AutoReleaseChoiceModal devPreview />;
      break;
    default:
      return null;
  }

  return (
    <>
      {modal}
      {/* Dev escape hatch — these steps are intentionally non-closable in the
          real flow (no ✕), so guarantee a way out of the preview. */}
      <button
        type="button"
        onClick={close}
        style={{
          position: "fixed",
          top: 14,
          left: 14,
          zIndex: 200000,
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.35)",
          background: "rgba(20,14,10,0.78)",
          color: "#fff",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
          fontSize: "0.8rem",
          fontWeight: 600,
          cursor: "pointer",
          backdropFilter: "blur(4px)",
        }}
      >
        ✕ Close preview
      </button>
    </>
  );
};

export default DevModalLauncher;
